import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { LL } from 'i18n/i18n.service';
import { ExerciseDiffEntry, ExerciseDiffField, diffValueText, isLongTextDiff } from 'services/exercise-diff';
import { fieldDiffLabel } from 'services/exercise-diff-ui';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { PreviewHelpText } from 'pages/help-card-page/help-card-page';
import { untranslatedString } from 'models/translated-string';
import { LoggingService } from 'services/logging.service';
import { ExerciseDiffPageParams, ExerciseDiffResult } from './exercise-diff-page-interface';

// Modal page presenting the per-field coach/source update diff; the user applies a selection,
// ignores, or backs out (cancel). It is a page (not a popover) so the long-description
// "Current/Updated description" links can push the full-screen HelpCardPage and Back cleanly
// returns here. `fullScreenOnMobile: false` (mirrors input-calendar-page) makes it a centered
// fixed-size modal on mobile too — so it stacks ON TOP of an underlying modal (e.g. the
// edit-exercise page) which stays visible behind it (cs-nav keeps the previous page
// `previous-visible` only for desktop-behavior modals), instead of replacing it full-screen.
// Width matches the old cs-exercise-diff-picker popover: 480 on desktop; on mobile the scss
// caps it at `100vw - 40px` (20px side margins, like the popover) so it never spans
// edge-to-edge (cs-nav's own cap is the full 100vw). Result is delivered via popAsync(result)
// -> returnCallback.
@Component({
  selector: 'page-exercise-diff',
  templateUrl: 'exercise-diff-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480, height: 'auto', fullScreenOnMobile: false }) }],
})
export class ExerciseDiffPage extends CsPage<ExerciseDiffPageParams, ExerciseDiffResult> {
  protected LL = LL;
  protected fieldDiffLabel = fieldDiffLabel;

  protected diff: ExerciseDiffEntry[] = [];

  // Take-the-update default: every diffed field pre-checked; unchecking is the exception.
  protected selected: Set<ExerciseDiffField> = new Set();

  constructor(
    public navCtrl: NavController,
    private cdRef: ChangeDetectorRef,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewWillEnter(): Promise<void> {
    const diff = this.navParams.data.local?.diff;
    // ionViewWillEnter ALSO re-fires when a "Current/Updated description" link's HelpCardPage
    // is popped back to this page (same instance). Re-running the pre-check would discard the
    // user's unchecks. The push params don't change across that round-trip, so the same `diff`
    // array reference means a pop-back — skip re-init and preserve `selected`. A genuinely new
    // open carries a fresh `diff` reference (new push params) and re-initialises.
    if (diff === this.diff) return;
    // all three callers push a non-empty diff in `local`; a missing/empty one is a wiring bug
    // (the page would render an empty list with Apply disabled) — surface it rather than
    // silently degrading.
    if (!diff || diff.length === 0) {
      this.logging.trackSilentProblem('ExerciseDiffPage opened with no diff', {
        hasLocal: String(!!this.navParams.data.local),
      });
    }
    this.diff = diff ?? [];
    this.selected = new Set(this.diff.map(d => d.field));
    this.cdRef.markForCheck();
  }

  // Open Decision: disable the Apply (n) button at zero rather than emitting an empty set the
  // caller would special-case as "ignore." The disabled button conveys "nothing to apply."
  get canApply(): boolean {
    return this.selected.size > 0;
  }

  isChecked(field: ExerciseDiffField): boolean {
    return this.selected.has(field);
  }

  onCheckboxChange(field: ExerciseDiffField, checked: boolean): void {
    if (checked) this.selected.add(field);
    else this.selected.delete(field);
    this.cdRef.markForCheck();
  }

  // `standard` is structured data — keep the collapsed "<X> changed" line. `description`
  // renders real before/after (inline if short, two full-page links if long); every other
  // field shows inline old -> new via oldDisplay/newDisplay.
  isCollapsedField(field: ExerciseDiffField): boolean {
    return field === 'standard';
  }

  isDescriptionField(field: ExerciseDiffField): boolean {
    return field === 'description';
  }

  descBefore(entry: ExerciseDiffEntry): string {
    return diffValueText(entry.oldValue);
  }
  descAfter(entry: ExerciseDiffEntry): string {
    return diffValueText(entry.newValue);
  }

  // Long when either side exceeds the inline cap -> show the two full-page links rather than
  // dumping the text into the row.
  isLongDescription(entry: ExerciseDiffEntry): boolean {
    return isLongTextDiff(entry);
  }

  // Push the full description onto HelpCardPage (same renderer used for exercise instructions),
  // reusing its `previewHelpText` mechanism. Because this is a modal page in the nav stack,
  // the push composes and Back returns here with the checkbox selection intact.
  private openDescriptionPage(title: string, text: string): void {
    const previewHelpText: PreviewHelpText = {
      title: untranslatedString(title),
      text: untranslatedString(text || LL.editExercise.alerts.updateAvailable.empty()),
    };
    window.localStorage.setItem('previewHelpText', JSON.stringify(previewHelpText));
    void this.navCtrl.pushAsync(IHelpCardPage, {});
  }

  openCurrentDescription(entry: ExerciseDiffEntry): void {
    this.openDescriptionPage(LL.editExercise.alerts.updateAvailable.pickerCurrentDescription(), this.descBefore(entry));
  }

  openUpdatedDescription(entry: ExerciseDiffEntry): void {
    this.openDescriptionPage(LL.editExercise.alerts.updateAvailable.pickerUpdatedDescription(), this.descAfter(entry));
  }

  applySelected(): void {
    if (!this.canApply) return;
    void this.navCtrl.popAsync<ExerciseDiffResult>({ action: 'apply', appliedFields: new Set(this.selected) });
  }

  // Explicit "Ignore" button — dismiss the update (caller marks the rev seen).
  ignore(): void {
    void this.navCtrl.popAsync<ExerciseDiffResult>({ action: 'ignore' });
  }
}
